import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  FormControl,
  Checkbox,
  FormControlLabel,
  IconButton,
  Card,
  CardContent,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Tooltip,
  Alert,
  AlertTitle
} from "@mui/material";
import {
  FaArrowLeft,
  FaPaperclip,
  FaTrash,
  FaArrowRight,
  FaEye
} from "react-icons/fa";
import { HiOutlineMail } from "react-icons/hi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  getData,
  postData,
  postFileUpload
} from "../../../Webservice/Webservice";
import { IoEyeOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";

function Email() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    customerIds: "",
    scheduledAt: null,
    subject: "",
    content: "",
    attachments: []
  });
  const [attachments, setAttachments] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filterData, setFilterData] = useState({
    promotionType: "email",
    city: [],
    gender: [],
    status: [],
    customerType: []
  });
  const [loading, setLoading] = useState(false);
  const [emailCampaigns, setEmailCampaigns] = useState([]); // State for API data
  const [error, setError] = useState(null); // State for error handling

  // Fetch cities from API
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await getData("campaign/city/list", null, token);
        setCities(response.cities || []);
      } catch (error) {
        console.error("Error fetching cities:", error);
        setCities([]);
      }
    };
    fetchCities();
  }, []);

  // Fetch email campaigns from API
  useEffect(() => {
    const fetchEmailCampaigns = async () => {
      setLoading(true);
      try {
        const tokenValue = sessionStorage.getItem("token");
        const token = tokenValue ? `Bearer ${tokenValue}` : "";
        const response = await getData("campaign/email/status", null, token);
        setEmailCampaigns(response || []);
        setError(null);
      } catch (error) {
        console.error("Error fetching email campaigns:", error);
        setEmailCampaigns([]);
        setError("Failed to load email campaigns. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchEmailCampaigns();
  }, []);

  const fetchCustomers = async filters => {
    setLoading(true);
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      const response = await postData("campaign/customer/list", filters, token);
      if (response && response.customers) {
        // Filter out customers without email and De-duplicate by email address
        const uniqueMap = new Map();
        response.customers.forEach(c => {
          if (c.email && c.email.trim() !== "" && !uniqueMap.has(c.email.toLowerCase())) {
            uniqueMap.set(c.email.toLowerCase(), c);
          }
        });
        const uniqueCustomers = Array.from(uniqueMap.values());

        setCustomers(uniqueCustomers);
        setSelectedCustomers(uniqueCustomers.map(customer => customer._id));
      } else {

        setCustomers([]);
        setSelectedCustomers([]);
      }
    } catch (error) {
      console.error("Error fetching customers:", error);
      setCustomers([]);
      setSelectedCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const { customerType, ...filtersWithoutCustomerType } = filterData;
    fetchCustomers(filtersWithoutCustomerType);
  }, []);

  // ReactQuill modules configuration
  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, 4, 5, 6, false] }],
      [{ font: [] }],
      [{ size: ["small", false, "large", "huge"] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ script: "sub" }, { script: "super" }],
      ["blockquote", "code-block"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["image"],
      ["clean"]
    ]
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "script",
    "blockquote",
    "code-block",
    "list",
    "bullet",
    "indent",
    "align",
    "link",
    "image",
    "video"
  ];

  const getStatusColor = status => {
    switch (status.toLowerCase()) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "scheduled":
        return "info";
      case "draft":
        return "default";
      default:
        return "default";
    }
  };

  const formatStatusText = status => {
    switch (status.toLowerCase()) {
      case "completed":
        return "Completed";
      case "pending":
        return "Pending";
      case "scheduled":
        return "Scheduled";
      case "draft":
        return "Draft";
      default:
        return status;
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilterData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCityChange = event => {
    const {
      target: { value }
    } = event;

    if (value[value.length - 1] === "all") {
      const allSelected = selectedCity.length === cities.length;
      const newSelected = allSelected ? [] : cities;
      setSelectedCity(newSelected);
      handleFilterChange("city", newSelected);
      return;
    }

    const selectedCities = typeof value === "string" ? value.split(",") : value;
    setSelectedCity(selectedCities);
    handleFilterChange("city", selectedCities);
  };

  const handleGenderChange = event => {
    const { target: { value } } = event;
    const selectedGenders =
      typeof value === "string" ? value.split(",") : value;
    handleFilterChange("gender", selectedGenders);
  };

  const handleStatusChange = event => {
    const { target: { value } } = event;
    const selectedStatus = typeof value === "string" ? value.split(",") : value;
    handleFilterChange("status", selectedStatus);
  };

  const handleCustomerTypeChange = event => {
    const { target: { value } } = event;
    const selectedCustomerTypes =
      typeof value === "string" ? value.split(",") : value;
    handleFilterChange("customerType", selectedCustomerTypes);
  };

  const handleDescriptionChange = value => {
    setFormData(prev => ({
      ...prev,
      content: value // Updated to match API payload
    }));
  };

  const handleFileUpload = e => {
    const files = Array.from(e.target.files);
    setAttachments(prev => [...prev, ...files]);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  const handleRemoveFile = index => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleScheduleChange = e => {
    const isChecked = e.target.checked;
    setFormData(prev => ({
      ...prev,
      isScheduled: isChecked,
      status: isChecked ? "Scheduled" : "Draft",
      date: isChecked ? prev.date : ""
    }));
  };

  const handleCustomerSelection = customerId => {
    setSelectedCustomers(
      prev =>
        prev.includes(customerId)
          ? prev.filter(id => id !== customerId)
          : [...prev, customerId]
    );
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleSubmitFilter = async e => {
    e.preventDefault();
    const { customerType, ...filtersWithoutCustomerType } = filterData;
    await fetchCustomers(filtersWithoutCustomerType);
  };

  const handleFormSubmit = async e => {
    e.preventDefault();
    const form = new FormData();
    form.append("name", formData.name);
    form.append("subject", formData.subject);
    form.append("content", formData.content || "");
    form.append("customerIds", JSON.stringify(selectedCustomers));
    if (formData.isScheduled) {
      form.append("scheduledAt", formData.date || "");
    }
    // Attach files
    if (formData.attachments && formData.attachments.length > 0) {
      formData.attachments.forEach((file, idx) => {
        form.append("attachments", file);
      });
    }
    try {
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      await postFileUpload("campaign/email/create", form, token, true); // true = multipart
      setFormData({
        name: "",
        date: "",
        totalCustomers: "",
        subject: "",
        content: "",
        status: "Draft",
        isScheduled: false,
        attachments: []
      });
      setAttachments([]);
      setFilterData({
        promotionType: "email",
        city: [],
        gender: [],
        status: [],
        customerType: []
      });
      setSelectedCity([]);
      setShowForm(false);
      // Refresh email campaigns after creating a new one
      const response = await getData("campaign/email/status", null, token);
      setEmailCampaigns(response || []);
    } catch (error) {
      console.error("Failed to create campaign:", error);
    }
  };

  const handleBack = () => {
    setShowForm(false);
    setFormData({
      name: "",
      date: "",
      totalCustomers: "",
      subject: "",
      content: "",
      status: "Draft",
      isScheduled: false,
      attachments: []
    });
    setAttachments([]);
    setFilterData({
      promotionType: "email",
      city: [],
      gender: [],
      status: [],
      customerType: []
    });
    setSelectedCity([]);
  };

  const getCustomerDisplayName = customer => {
    if (customer.email) return customer.email;
    if (customer.mobile) return customer.mobile;
    return `Customer ${customer._id}`;
  };

  if (showForm) {
    return (
      <Box sx={{ p: 4, maxWidth: 1200, margin: "0 auto" }}>
        <Card
          sx={{
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            border: "1px solid #d3d3d3de"
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Box display="flex" alignItems="center" mb={4}>
              <IconButton
                onClick={handleBack}
                sx={{
                  mr: 2,
                  color: "black",
                  "&:hover": { backgroundColor: "rgba(255, 209, 2, 0.2)" }
                }}
              >
                <FaArrowLeft />
              </IconButton>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontFamily: "Mulish, sans-serif", fontWeight: 600 }}
              >
                Add New Campaign
              </Typography>
            </Box>
            <form onSubmit={handleFormSubmit}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <TextField
                    required
                    label="Campaign Title"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    sx={{
                      flex: "1 1 300px",
                      "& .MuiOutlinedInput-root": { borderRadius: "8px" }
                    }}
                  />
                  {formData.isScheduled &&
                    <TextField
                      required
                      label="Schedule Date & Time"
                      name="date"
                      type="datetime-local"
                      value={formData.date}
                      onChange={handleInputChange}
                      sx={{
                        flex: "1 1 200px",
                        "& .MuiOutlinedInput-root": { borderRadius: "8px" }
                      }}
                      InputLabelProps={{ shrink: true }}
                    />}
                  <FormControl sx={{ flex: "1 1 200px", alignSelf: "center" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.isScheduled}
                          onChange={handleScheduleChange}
                          name="isScheduled"
                        />
                      }
                      label="Set Schedule"
                      sx={{ fontFamily: "Mulish, sans-serif" }}
                    />
                  </FormControl>
                </Box>

                <Box>
                  <Typography
                    sx={{ fontSize: "1rem", color: "text.primary", mb: 2 }}
                  >
                    Customers Section
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Promotion Type</InputLabel>
                        <Select
                          label="Promotion Type"
                          value={filterData.promotionType}
                          onChange={e =>
                            handleFilterChange("promotionType", e.target.value)}
                          sx={{ borderRadius: "8px" }}
                        >
                          <MenuItem value="email">Email</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>City</InputLabel>
                        <Select
                          label="City"
                          multiple
                          value={selectedCity}
                          onChange={handleCityChange}
                          sx={{ borderRadius: "8px" }}
                          renderValue={selected =>
                            selected.length === cities.length &&
                            cities.length > 0
                              ? "All Cities"
                              : selected.join(", ")}
                        >
                          <MenuItem value="all">
                            <Checkbox
                              checked={
                                cities.length > 0 &&
                                selectedCity.length === cities.length
                              }
                              indeterminate={
                                selectedCity.length > 0 &&
                                selectedCity.length < cities.length
                              }
                            />
                            <ListItemText primary="Select All" />
                          </MenuItem>
                          {cities.map((city, index) =>
                            <MenuItem key={index} value={city}>
                              <Checkbox checked={selectedCity.includes(city)} />
                              <ListItemText primary={city} />
                            </MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Gender</InputLabel>
                        <Select
                          label="Gender"
                          multiple
                          value={filterData.gender}
                          onChange={handleGenderChange}
                          sx={{ borderRadius: "8px" }}
                          renderValue={selected => selected.join(", ")}
                        >
                          <MenuItem value="male">
                            <Checkbox
                              checked={filterData.gender.includes("male")}
                            />
                            <ListItemText primary="Male" />
                          </MenuItem>
                          <MenuItem value="female">
                            <Checkbox
                              checked={filterData.gender.includes("female")}
                            />
                            <ListItemText primary="Female" />
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Customer Type</InputLabel>
                        <Select
                          label="Customer Type"
                          multiple
                          value={filterData.customerType}
                          onChange={handleCustomerTypeChange}
                          sx={{ borderRadius: "8px" }}
                          renderValue={selected => selected.join(", ")}
                        >
                          <MenuItem value="customer">
                            <Checkbox
                              checked={filterData.customerType.includes(
                                "customer"
                              )}
                            />
                            <ListItemText primary="Customer" />
                          </MenuItem>
                          <MenuItem value="business">
                            <Checkbox
                              checked={filterData.customerType.includes(
                                "business"
                              )}
                            />
                            <ListItemText primary="Business" />
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select
                          label="Status"
                          multiple
                          value={filterData.status}
                          onChange={handleStatusChange}
                          sx={{ borderRadius: "8px" }}
                          renderValue={selected => selected.join(", ")}
                        >
                          <MenuItem value="active">
                            <Checkbox
                              checked={filterData.status.includes("active")}
                            />
                            <ListItemText primary="Active" />
                          </MenuItem>
                          <MenuItem value="inactive">
                            <Checkbox
                              checked={filterData.status.includes("inactive")}
                            />
                            <ListItemText primary="Inactive" />
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Button
                        type="button"
                        onClick={handleSubmitFilter}
                        disabled={loading}
                        sx={{
                          mt: 1,
                          border: "1px solid black",
                          color: "grey",
                          backgroundColor: "#ece4e454",
                          textTransform: "none"
                        }}
                      >
                        {loading ? "Loading..." : "Apply Filters"}
                        <FaArrowRight style={{ marginLeft: "8px" }} />
                      </Button>
                    </Grid>
                  </Grid>
                  <Typography
                    sx={{
                      fontSize: "0.9rem",
                      color: "text.secondary",
                      mt: 1,
                      alignSelf: "center",
                      justifyContent: "center",
                      display: "flex",
                      gap: 1,
                      fontFamily: "Mulish, sans-serif",
                      mt: 5
                    }}
                  >
                    <Button onClick={handleOpenModal}>
                      <FaEye size={23} />
                      Total selected Customers: {selectedCustomers.length || 0}
                    </Button>
                  </Typography>
                </Box>

                <TextField
                  required
                  label="Subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  fullWidth
                  variant="outlined"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
                <Alert 
                  severity="info" 
                  sx={{ 
                    borderRadius: "8px", 
                    "& .MuiAlert-message": { fontFamily: "Mulish, sans-serif" } 
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 700 }}>Spam Prevention Tip</AlertTitle>
                  Avoid using <strong>ALL CAPS</strong>, excessive exclamation marks (<strong>!!!</strong>), or words like <strong>"FREE"</strong>, <strong>"WIN"</strong>, or <strong>"CASH"</strong> in your subject line to prevent your email from landing in the spam folder.
                </Alert>

                <Typography sx={{ fontSize: "1rem", color: "text.primary" }}>
                  Content
                </Typography>
                <FormControl fullWidth>
                  <Box
                    sx={{
                      border: "1px solid #c4c4c4",
                      borderRadius: "8px",
                      overflow: "hidden",
                      "& .ql-toolbar": {
                        border: "none",
                        borderBottom: "1px solid #e0e0e0",
                        backgroundColor: "#f8f9fa"
                      },
                      "& .ql-container": {
                        border: "none",
                        fontFamily: "Mulish, sans-serif",
                        fontSize: "1rem",
                        minHeight: "200px"
                      },
                      "& .ql-editor": {
                        minHeight: "200px",
                        fontFamily: "Mulish, sans-serif"
                      },
                      "&:hover": { border: "1px solid #000" },
                      "&.ql-focused": { border: "2px solid #1976d2" }
                    }}
                  >
                    <ReactQuill
                      value={formData.content}
                      onChange={handleDescriptionChange}
                      modules={modules}
                      formats={formats}
                      theme="snow"
                      placeholder="Write your email content here..."
                    />
                  </Box>
                </FormControl>
                <Alert 
                  severity="warning" 
                  sx={{ 
                    borderRadius: "8px", 
                    "& .MuiAlert-message": { fontFamily: "Mulish, sans-serif" } 
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 700 }}>Content Quality Check</AlertTitle>
                  To avoid spam filters, ensure your content has a good <strong>Text-to-Image ratio</strong>. Avoid using too many external links, hidden text, or deceptive phrases like <strong>"Click here to claim your prize"</strong>.
                </Alert>


                <Box>
                  <Typography
                    variant="h6"
                    sx={{ mb: 2, fontFamily: "Mulish, sans-serif" }}
                  >
                    Attachments
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<FaPaperclip />}
                    sx={{
                      mb: 2,
                      borderRadius: "8px",
                      textTransform: "none",
                      fontFamily: "Mulish, sans-serif"
                    }}
                  >
                    Upload Files
                    <input
                      type="file"
                      multiple
                      hidden
                      onChange={handleFileUpload}
                    />
                  </Button>
                  {attachments.map((file, index) =>
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1.5,
                        mb: 1,
                        backgroundColor: "#f8f9fa",
                        borderRadius: "6px",
                        border: "1px solid #e0e0e0"
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <FaPaperclip size={14} color="#666" />
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "Mulish, sans-serif",
                            fontWeight: 500
                          }}
                        >
                          {file.name}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveFile(index)}
                        sx={{
                          color: "error.main",
                          "&:hover": {
                            backgroundColor: "rgba(211, 47, 47, 0.1)"
                          }
                        }}
                      >
                        <FaTrash size={14} />
                      </IconButton>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    justifyContent: "flex-end",
                    mt: 2
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      fontFamily: "Mulish, sans-serif",
                      fontWeight: 600,
                      px: 4,
                      border: "1px solid #d3d3d3",
                      "&:hover": {
                        border: "1px solid #666",
                        backgroundColor: "rgba(0,0,0,0.02)"
                      }
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      backgroundColor: "rgb(255, 209, 2)",
                      border: "1px solid black",
                      color: "black",
                      padding: "8px 32px",
                      borderRadius: "8px",
                      textTransform: "none",
                      fontWeight: 600,
                      fontFamily: "Mulish, sans-serif",
                      fontSize: "0.9rem",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "rgb(230, 189, 0)",
                        transform: "translateY(-2px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
                      }
                    }}
                  >
                    Create Campaign
                  </Button>
                </Box>
              </Box>
            </form>

            <Dialog
              open={openModal}
              onClose={handleCloseModal}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle
                sx={{ fontFamily: "Mulish, sans-serif", fontWeight: 600 }}
              >
                Selected Customers ({selectedCustomers.length})
              </DialogTitle>
              <DialogContent>
                <List>
                  {customers.map(customer =>
                    <ListItem key={customer._id} sx={{ py: 1 }}>
                      <ListItemIcon>
                        <Checkbox
                          checked={selectedCustomers.includes(customer._id)}
                          onChange={() => handleCustomerSelection(customer._id)}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={getCustomerDisplayName(customer)}
                        primaryTypographyProps={{
                          fontFamily: "Mulish, sans-serif"
                        }}
                        secondaryTypographyProps={{
                          fontFamily: "Mulish, sans-serif"
                        }}
                      />
                    </ListItem>
                  )}
                </List>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCloseModal}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontFamily: "Mulish, sans-serif",
                    fontWeight: 600
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, maxWidth: 1200, margin: "0 auto" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              color: "#1a202c",
              fontFamily: "Mulish, sans-serif"
            }}
          >
            Email Campaign
          </Typography>
          <Chip
            label={`${emailCampaigns.length} ${emailCampaigns.length === 1 ? 'Campaign' : 'Campaigns'}`}
            size="small"
            sx={{
                backgroundColor: 'rgba(255, 209, 2, 0.2)',
                color: '#b39200',
                fontFamily: 'Mulish, sans-serif',
                fontWeight: '800',
                border: '1px solid rgba(255, 209, 2, 0.5)'
            }}
          />
        </Box>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Button
            variant="contained"
            onClick={() => setShowForm(true)}
            startIcon={<HiOutlineMail />}
            sx={{
              fontFamily: 'Mulish, sans-serif',
              background: 'rgb(255 209 2)',
              color: 'black',
              textTransform: 'none',
              borderRadius: '10px',
              fontWeight: 700,
              fontSize: '0.9rem',
              px: 3,
              boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)',
              border: '1px solid black',
              transition: 'all 0.2s ease',
              '&:hover': {
                  background: 'rgb(255 209 2)',
                  boxShadow: '0 6px 16px rgba(255, 209, 2, 0.4)',
                  transform: 'translateY(-1px)'
              }
            }}
          >
            Add New Campaign
          </Button>
        </Box>
      </Box>

      {loading
        ? <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        : error
          ? <Typography color="error" sx={{ textAlign: "center", py: 4 }}>
              {error}
            </Typography>
          : <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                borderRadius: "8px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                border: "1px solid #d3d3d3de"
              }}
            >
              <Table sx={{ minWidth: 750 }} stickyHeader aria-label="email campaigns table">
                <TableHead>
                  <TableRow sx={{ 
                    '& th': {
                      backgroundColor: '#f8f9fa',
                      fontWeight: 800,
                      py: 2,
                      fontSize: '0.75rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      color: '#5c636a',
                      fontFamily: 'Mulish, sans-serif',
                      borderBottom: '2px solid #edeff1',
                    }
                  }}>
                    <TableCell>Title</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Total Customers</TableCell>
                    <TableCell>Subject</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emailCampaigns.length === 0
                    ? <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            <img 
                              src="./img/nodata.png" 
                              alt="No campaigns" 
                              style={{ width: '180px' }} 
                            />  
                            <Typography variant="h6" sx={{ fontFamily: 'Mulish, sans-serif', color: 'text.secondary' }}>
                              No email campaigns found.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    : emailCampaigns.map(campaign =>
                        <TableRow
                          key={campaign._id}
                          hover
                          onClick={() => navigate(`/email/${campaign._id}`)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#f9fafb" },
                            transition: "background-color 0.2s ease"
                          }}
                        >
                          <TableCell>
                            <Typography 
                              fontWeight={700} 
                              sx={{ 
                                fontFamily: 'Mulish, sans-serif', 
                                fontSize: '0.9rem',
                                color: '#1a1c1e'
                              }}
                            >
                              {campaign.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'Mulish, sans-serif', color: '#4b5563' }}>
                              {new Date(campaign.createdAt).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              fontWeight={700} 
                              sx={{ 
                                fontFamily: 'Mulish, sans-serif', 
                                fontSize: '0.9rem',
                                color: '#1a1c1e'
                              }}
                            >
                              {campaign.stats.total.toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontFamily: 'Mulish, sans-serif', 
                                color: '#6b7280',
                                maxWidth: '250px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {campaign.subject}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={formatStatusText(campaign.status)}
                              size="small"
                              sx={{
                                fontFamily:'Mulish, sans-serif',
                                fontWeight: 700,
                                fontSize: '0.65rem',
                                textTransform: 'uppercase',
                                borderRadius: '6px',
                                backgroundColor: campaign.status.toLowerCase() === 'completed' ? '#def7ec' : '#fef3c7',
                                color: campaign.status.toLowerCase() === 'completed' ? '#03543f' : '#92400e',
                                border: 'none'
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="View Details" arrow>
                              <IconButton
                                size="small"
                                sx={{ 
                                  color: '#4b5563',
                                  backgroundColor: 'rgba(75, 85, 99, 0.1)',
                                  '&:hover': { 
                                    backgroundColor: 'rgba(255, 209, 2, 0.2)',
                                    color: '#b39200'
                                  },
                                  borderRadius: '8px'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/email/${campaign._id}`);
                                }}
                              >
                                <IoEyeOutline size={20} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      )}
                </TableBody>
              </Table>
            </TableContainer>}
    </Box>
  );
}

export default Email;
